import { ReactNode } from "react";
import styles from "./States.module.css";

interface EmptyProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  tone?: "default" | "error";
}

/** Designed empty/error state — replaces bare <Spin/> and blank tables.
 *  Flush left, one 2px accent rule, no illustration. */
export const EmptyState = ({ title, description, actions, tone = "default" }: EmptyProps) => (
  <div className={`${styles.empty} ${tone === "error" ? styles.error : ""}`}>
    <div className={styles.emptyTitle}>{title}</div>
    {description && <p className={styles.emptyBody}>{description}</p>}
    {actions && <div className={styles.emptyActions}>{actions}</div>}
  </div>
);

/** Table-shaped loading skeleton: the page keeps its geometry while it loads,
 *  so nothing jumps when data lands. */
export const TableSkeleton = ({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) => {
  const widths = ["38%", "18%", "22%", "14%", "20%", "16%"];
  return (
    <div className={styles.skelWrap} aria-busy="true" aria-label="Loading">
      <div className={`${styles.skelRow} ${styles.skelHead}`}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className={styles.bar} style={{ width: widths[i % widths.length], opacity: 0.7 }} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div className={styles.skelRow} key={r}>
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} className={styles.bar} style={{ width: widths[(i + r) % widths.length] }} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton = ({ height = 96 }: { height?: number }) => (
  <div className={styles.skelWrap} style={{ height, padding: 16 }} aria-busy="true">
    <div className={styles.bar} style={{ width: "40%", marginBottom: 12 }} />
    <div className={styles.bar} style={{ width: "70%" }} />
  </div>
);
